'use client';

import FilterListIcon from '@mui/icons-material/FilterList';
import { Box, Button, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  IconCurrencyDollar,
  IconSortAscendingLetters,
  IconSortDescendingLetters,
  IconTrendingDown,
} from '@tabler/icons-react';
import { IconLayoutGrid, IconLayoutList } from '@tabler/icons-react';
import { useEffect } from 'react';

import { NumberInputField } from '@/components/Inputs/NumberInputField';
import { SelectField } from '@/components/Inputs/SelectField';

import { AdvancedFilters } from './AdvancedFilters';

const sortOptions = [
  {
    value: 'interestLowToHigh',
    label: 'Lowest starting interest rate',
    icon: <IconTrendingDown size={16} />,
  },
  {
    value: 'amountHighToLow',
    label: 'Highest loan amount',
    icon: <IconCurrencyDollar size={16} />,
  },
  {
    value: 'providerAZ',
    label: 'Provider name (A - Z)',
    icon: <IconSortAscendingLetters size={16} />,
  },
  {
    value: 'providerZA',
    label: 'Provider name (Z - A)',
    icon: <IconSortDescendingLetters size={16} />,
  },
];

interface FilterBarProps {
  lenders: string[];
  purposes: string[];
  advanced: AdvancedFilters;
  defaultLoanAmount?: number | null;
  onToggleView: (_mode: 'grid' | 'list') => void;
  setAdvanced: (_filters: AdvancedFilters) => void;
  sortBy: string;
  setSortBy: (_val: string) => void;
  view: 'list' | 'grid';
  setView: (_val: 'list' | 'grid') => void;
  onOpenAdvanced: () => void;
  onClear: () => void;
}

export default function FilterBar({
  purposes,
  advanced,
  defaultLoanAmount,
  view,
  onToggleView,
  setAdvanced,
  sortBy,
  setSortBy,
  onOpenAdvanced,
  onClear,
}: FilterBarProps) {
  useEffect(() => {
    if (
      typeof defaultLoanAmount === 'number' &&
      (advanced.loanAmount[0] ?? 0) === 0 &&
      (advanced.loanAmount[1] ?? 0) > 0 &&
      defaultLoanAmount <= (advanced.loanAmount[1] ?? Infinity)
    ) {
      const newFilters: AdvancedFilters = {
        ...advanced,
        loanAmount: [defaultLoanAmount, advanced.loanAmount[1]],
      };

      setAdvanced(newFilters);
    }
  }, [defaultLoanAmount, advanced, setAdvanced]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        gap: 2.5,
        py: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 2.5,
        }}
      >
        {/* Loan purpose */}
        <SelectField
          label="Loan purpose"
          value={advanced.purposes[0] || ''}
          options={purposes.map((p) => ({ label: p, value: p }))}
          onChange={(val) =>
            setAdvanced({
              ...advanced,
              purposes: Array.isArray(val) ? val : [val],
            })
          }
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 200 }}
        />

        {/* Loan amount */}
        <NumberInputField
          label="Loan amount"
          value={advanced.loanAmount[0] || ''}
          onChange={(val) =>
            setAdvanced({
              ...advanced,
              loanAmount: [val, advanced.loanAmount[1]],
            })
          }
          currency
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 180 }}
        />
      </Box>
      {/* Sort by */}
      <SelectField
        label="Sort By"
        value={sortBy}
        variant="button"
        options={sortOptions}
        onChange={(val) => setSortBy(Array.isArray(val) ? val[0] : val)}
        sx={{
          minWidth: 120,
          width: { xs: '100%', sm: 'auto' },
        }}
      />

      {/* Filters button */}
      <Button
        onClick={onOpenAdvanced}
        variant="contained"
        color="primary"
        startIcon={<FilterListIcon />}
        sx={{
          width: { xs: '100%', sm: 'auto' },
          px: 3,
          whiteSpace: 'nowrap',
          minWidth: 120,
        }}
      >
        Filters
      </Button>

      {/* Clear button */}
      <Button
        onClick={onClear}
        variant="outlined"
        sx={{
          width: { xs: '100%', sm: 'auto' },
          px: 3,
          lineHeight: 1.375, // keep button heights consistent
          whiteSpace: 'nowrap',
        }}
      >
        Clear
      </Button>
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, marginLeft: 'auto' }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, newView) => {
            if (newView) onToggleView(newView);
          }}
          size="small"
        >
          <ToggleButton value="grid" aria-label="Grid View">
            <IconLayoutGrid size={18} />
          </ToggleButton>
          <ToggleButton value="list" aria-label="List View">
            <IconLayoutList size={18} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </Box>
  );
}
